import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { ImageCacheService } from '../../../services/image-cache.service';

@Component({
    selector: 'app-image-with-loader',
    templateUrl: './image-with-loader.component.html',
    styleUrls: ['./image-with-loader.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ImageWithLoaderComponent implements OnInit, OnDestroy, OnChanges {

  @Input() src: string = '';
  @Input() imageUrl: string = ''; // Alternative input name for compatibility
  @Input() alt: string = 'Image';
  @Input() title: string = '';
  @Input() className: string = '';
  @Input() loadingText: string = 'IMAGE_WITH_LOADER.LOADING';
  @Input() errorText: string = 'IMAGE_WITH_LOADER.ERROR';
  @Input() retryText: string = 'IMAGE_WITH_LOADER.RETRY';
  
  @Output() onLoad = new EventEmitter<string>();
  @Output() onError = new EventEmitter<string>();

  isLoading: boolean = true;
  hasError: boolean = false;
  cachedSrc: string = '';
  
  private imgElement: HTMLImageElement | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private imageCacheService: ImageCacheService
  ) { }

  ngOnInit(): void {
    this.getCachedImageAndLoad();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  ngOnChanges(): void {
    // When src or imageUrl changes, reload the image
    if (this.src || this.imageUrl) {
      this.getCachedImageAndLoad();
    }
  }

  private getCachedImageAndLoad(): void {
    const imageSrc = this.imageUrl || this.src;
    
    if (!imageSrc) {
      this.hasError = true;
      this.isLoading = false;
      this.onError.emit(imageSrc);
      return;
    }

    // Direkt yükle, cache directive eğer CDN ise halledicek
    this.loadImage(imageSrc);
  }

  private loadImage(imageSrc: string): void {
    if (!imageSrc) {
      this.hasError = true;
      this.isLoading = false;
      this.onError.emit(imageSrc);
      return;
    }

    this.cleanup();
    this.resetState();

    this.imgElement = new Image();
    
    const handleLoad = () => {
      this.isLoading = false;
      this.hasError = false;
      const imageSrc = this.cachedSrc || this.imageUrl || this.src;
      this.onLoad.emit(imageSrc);
      this.cdr.detectChanges();
    };

    const handleError = () => {
      this.isLoading = false;
      this.hasError = true;
      const imageSrc = this.cachedSrc || this.imageUrl || this.src;
      console.error('Image failed to load:', imageSrc);
      this.onError.emit(imageSrc);
      this.cdr.detectChanges();
    };

    this.imgElement.addEventListener('load', handleLoad);
    this.imgElement.addEventListener('error', handleError);

    this.imgElement.src = imageSrc;

    // If image is already cached and complete
    if (this.imgElement.complete) {
      if (this.imgElement.naturalWidth === 0) {
        handleError();
      } else {
        handleLoad();
      }
    }
  }

  private resetState(): void {
    this.isLoading = true;
    this.hasError = false;
    this.cdr.detectChanges();
  }

  private cleanup(): void {
    if (this.imgElement) {
      // Remove event listeners if they exist
      this.imgElement.onload = null;
      this.imgElement.onerror = null;
      this.imgElement = null;
    }
  }

  // Method to retry loading the image
  retryLoad(): void {
    this.loadImage(this.cachedSrc || this.imageUrl || this.src);
  }

  // Public methods for template binding
  handleLoad(): void {
    this.isLoading = false;
    this.hasError = false;
    const imageSrc = this.cachedSrc || this.imageUrl || this.src;
    this.onLoad.emit(imageSrc);
    this.cdr.detectChanges();
  }
}